/** @format */

import { useEffect, useMemo, useState } from 'react';

import { error } from '../../Shared/Components/Toast';

const MIN_SLOTS = 2;
const MAX_SLOTS = 6;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024;

let slotCounter = 0;
const createSlotKey = () => `slot-${++slotCounter}-${Date.now()}`;

const buildInitialRows = (existingImages = []) => {
  const rows = existingImages.map((image) => ({
    key: `existing-${image.id}`,
    kind: 'existing',
    existingId: image.id,
    existingUrl: image.url,
    sortOrder: image.sort_order,
    file: null,
    previewUrl: null,
  }));

  while (rows.length < MIN_SLOTS) {
    rows.push({
      key: createSlotKey(),
      kind: 'new',
      file: null,
      previewUrl: null,
    });
  }

  return rows;
};

const ClubImageUploader = ({ existingImages = [], onChange }) => {
  const [rows, setRows] = useState(() => buildInitialRows(existingImages));
  const [removedImageIds, setRemovedImageIds] = useState([]);

  const existingKey = existingImages.map((img) => img.id).join(',');

  useEffect(() => {
    setRows(buildInitialRows(existingImages));
    setRemovedImageIds([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingKey]);

  useEffect(() => {
    const previews = rows.map((row) => row.previewUrl).filter(Boolean);
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [rows]);

  const visibleExisting = useMemo(
    () => existingImages.filter((img) => !removedImageIds.includes(img.id)),
    [existingImages, removedImageIds],
  );

  const activeRows = useMemo(() => {
    return rows.filter(
      (row) =>
        row.kind === 'new' ||
        (row.kind === 'existing' && !removedImageIds.includes(row.existingId)),
    );
  }, [rows, removedImageIds]);

  const filledCount = useMemo(() => {
    const existingCount = visibleExisting.length;
    const newCount = activeRows.filter((row) => row.kind === 'new' && row.file).length;
    return existingCount + newCount;
  }, [visibleExisting, activeRows]);

  useEffect(() => {
    const files = activeRows
      .filter((row) => row.kind === 'new' && row.file)
      .map((row) => row.file);

    onChange({
      files,
      removeImageIds: removedImageIds,
      filledCount,
      slotCount: activeRows.length,
    });
  }, [activeRows, removedImageIds, filledCount, onChange]);

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      error('فرمت فایل باید jpg، png یا webp باشد');
      return false;
    }

    if (file.size > MAX_SIZE) {
      error('حجم هر تصویر نباید بیشتر از ۵ مگابایت باشد');
      return false;
    }

    return true;
  };

  const handleSelectFile = (rowKey, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    if (!validateFile(file)) {
      return;
    }

    setRows((prev) =>
      prev.map((row) => {
        if (row.key !== rowKey) {
          return row;
        }

        if (row.previewUrl) {
          URL.revokeObjectURL(row.previewUrl);
        }

        return {
          ...row,
          file,
          previewUrl: URL.createObjectURL(file),
        };
      }),
    );
  };

  const ensureMinSlots = (nextRows, nextRemovedIds) => {
    const activeCount = nextRows.filter(
      (item) =>
        item.kind === 'new' ||
        (item.kind === 'existing' && !nextRemovedIds.includes(item.existingId)),
    ).length;

    if (activeCount >= MIN_SLOTS) {
      return nextRows;
    }

    const extra = Array.from({ length: MIN_SLOTS - activeCount }, () => ({
      key: createSlotKey(),
      kind: 'new',
      file: null,
      previewUrl: null,
    }));

    return [...nextRows, ...extra];
  };

  const handleRemoveRow = (row) => {
    if (row.kind === 'existing') {
      setRemovedImageIds((prev) => {
        const nextRemoved = [...prev, row.existingId];
        setRows((current) => ensureMinSlots(current, nextRemoved));
        return nextRemoved;
      });
      return;
    }

    if (row.previewUrl) {
      URL.revokeObjectURL(row.previewUrl);
    }

    if (activeRows.length <= MIN_SLOTS) {
      setRows((prev) =>
        prev.map((item) =>
          item.key === row.key
            ? { ...item, file: null, previewUrl: null }
            : item,
        ),
      );
      return;
    }

    setRows((prev) => {
      const filtered = prev.filter((item) => item.key !== row.key);
      return ensureMinSlots(filtered, removedImageIds);
    });
  };

  const handleAddRow = () => {
    if (activeRows.length >= MAX_SLOTS) {
      error('حداکثر ۶ تصویر مجاز است');
      return;
    }

    setRows((prev) => [
      ...prev,
      {
        key: createSlotKey(),
        kind: 'new',
        file: null,
        previewUrl: null,
      },
    ]);
  };

  const getPreview = (row) => {
    if (row.kind === 'existing') {
      return row.existingUrl;
    }

    return row.previewUrl;
  };

  return (
    <div className='club-image-uploader'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <label className='f-13 mb-0'>
          تصاویر باشگاه
          <span className='text-muted f-12 ms-1'>(حداقل ۲، حداکثر ۶)</span>
        </label>
        <span className='badge bg-light text-dark border f-12'>
          {filledCount} / {MAX_SLOTS}
        </span>
      </div>

      <div className='d-flex flex-column gap-2'>
        {activeRows.map((row, index) => {
          const preview = getPreview(row);

          return (
            <div
              key={row.key}
              className='d-flex align-items-center gap-2 p-2 border rounded bg-white'
            >
              <span className='text-muted f-12' style={{ minWidth: 24 }}>
                {index + 1}
              </span>

              {preview ? (
                <div
                  className='position-relative border rounded overflow-hidden flex-shrink-0'
                  style={{ width: 72, height: 72 }}
                >
                  <img
                    src={preview}
                    alt={`club-${index + 1}`}
                    className='w-100 h-100 object-fit-cover'
                  />
                </div>
              ) : (
                <div
                  className='d-flex align-items-center justify-content-center border rounded bg-light text-muted flex-shrink-0 f-11'
                  style={{ width: 72, height: 72 }}
                >
                  بدون تصویر
                </div>
              )}

              {row.kind === 'new' && !preview && (
                <label className='btn btn-outline-secondary btn-sm f-12 mb-0 flex-grow-1 text-end'>
                  انتخاب تصویر
                  <input
                    type='file'
                    className='d-none'
                    accept='image/jpeg,image/jpg,image/png,image/webp'
                    onChange={(e) => handleSelectFile(row.key, e)}
                  />
                </label>
              )}

              {row.kind === 'new' && preview && (
                <label className='btn btn-outline-primary btn-sm f-12 mb-0'>
                  تغییر تصویر
                  <input
                    type='file'
                    className='d-none'
                    accept='image/jpeg,image/jpg,image/png,image/webp'
                    onChange={(e) => handleSelectFile(row.key, e)}
                  />
                </label>
              )}

              {row.kind === 'existing' && (
                <span className='text-muted f-12 flex-grow-1'>
                  تصویر فعلی باشگاه
                </span>
              )}

              <button
                type='button'
                className='btn btn-outline-danger btn-sm f-12'
                onClick={() => handleRemoveRow(row)}
                title='حذف'
              >
                <i className='fad fa-trash-alt'></i>
              </button>
            </div>
          );
        })}
      </div>

      {activeRows.length < MAX_SLOTS && (
        <button
          type='button'
          className='btn btn-outline-success btn-sm mt-2 f-12'
          onClick={handleAddRow}
        >
          <i className='fad fa-plus ms-1'></i>
          افزودن تصویر
        </button>
      )}

      {filledCount < MIN_SLOTS && (
        <div className='text-danger f-12 mt-2'>
          حداقل ۲ تصویر الزامی است ({MIN_SLOTS - filledCount} تصویر دیگر نیاز است)
        </div>
      )}
    </div>
  );
};

export default ClubImageUploader;
